import React, { useEffect, useId, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reviewInvitationService } from '../../services/reviewInvitationService';
import type { RepairRequest } from '../../types/firestore';
import type { ReviewInvitation } from '../../types/reviewInvitation';
import {
  getReviewInvitationCreatedAtMs,
  isReviewInvitationPromptable,
  timestampToMillis,
} from '../../utils/reviewInvitationEligibility';
import { Button, Modal } from './UIComponents';
import { useGlobalPromptArbiter } from './GlobalPromptArbiter';

interface ReviewInvitationContextState {
  request: RepairRequest | null;
  canonicalReviewExists: boolean;
  ready: boolean;
}

const MAX_EXPIRY_TIMER_MS = 20 * 24 * 60 * 60 * 1000;

const ReviewInvitationPrompt: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerCandidate, unregisterCandidate, activePromptId } = useGlobalPromptArbiter();
  const [invitations, setInvitations] = useState<ReviewInvitation[]>([]);
  const [contexts, setContexts] = useState<Record<string, ReviewInvitationContextState>>({});
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [snoozingRequestId, setSnoozingRequestId] = useState<string | null>(null);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  const modalTitleId = useId();
  const isActiveClient =
    Boolean(currentUser?.uid) && userProfile?.role === 'client' && userProfile.status === 'active';

  useEffect(() => {
    if (!isActiveClient || !currentUser?.uid) {
      setInvitations([]);
      setContexts({});
      return undefined;
    }

    const unsubscribe = reviewInvitationService.subscribeToOwnPendingReviewInvitations(
      currentUser.uid,
      setInvitations,
      (error) => {
        console.error('[ReviewInvitationPrompt] Pending invitation subscription failed.', error);
        setInvitations([]);
      }
    );
    return unsubscribe;
  }, [currentUser?.uid, isActiveClient]);

  const invitationRequestIds = useMemo(
    () =>
      Array.from(
        new Set(
          invitations
            .map((invitation) =>
              typeof invitation.requestId === 'string' ? invitation.requestId.trim() : ''
            )
            .filter(Boolean)
        )
      ).sort(),
    [invitations]
  );
  useEffect(() => {
    if (!isActiveClient) {
      setContexts({});
      return undefined;
    }

    const activeIds = new Set(invitationRequestIds);
    setContexts((previous) => {
      const next = Object.fromEntries(
        Object.entries(previous).filter(([requestId]) => activeIds.has(requestId))
      );
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });

    const unsubscribers = invitationRequestIds.map((requestId) =>
      reviewInvitationService.subscribeToReviewInvitationContext(
        requestId,
        (context) => {
          setContexts((previous) => ({
            ...previous,
            [requestId]: { ...context, ready: true },
          }));
        },
        (error) => {
          console.error(
            `[ReviewInvitationPrompt] Context subscription failed for ${requestId}.`,
            error
          );
          setContexts((previous) => ({
            ...previous,
            [requestId]: { ...previous[requestId], ready: false },
          }));
        }
      )
    );

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [invitationRequestIds, isActiveClient]);

  useEffect(() => {
    if (!isActiveClient) return undefined;
    const futureExpiryTimes = invitations
      .map((invitation) => timestampToMillis(invitation.snoozedUntil))
      .filter((value): value is number => value !== null && value > nowMs);
    if (futureExpiryTimes.length === 0) return undefined;

    const nextExpiryMs = Math.min(...futureExpiryTimes);
    const timeoutMs = Math.min(Math.max(1, nextExpiryMs - nowMs + 1), MAX_EXPIRY_TIMER_MS);
    const timeoutId = window.setTimeout(() => setNowMs(Date.now()), timeoutMs);
    return () => window.clearTimeout(timeoutId);
  }, [invitations, isActiveClient, nowMs]);

  const selectedInvitation = useMemo(() => {
    if (!isActiveClient || !currentUser?.uid) return null;

    return (
      invitations
        .filter((invitation) => {
          const requestId =
            typeof invitation.requestId === 'string' ? invitation.requestId.trim() : '';
          const context = requestId ? contexts[requestId] : undefined;
          return Boolean(
            context?.ready &&
              isReviewInvitationPromptable({
                invitation,
                request: context.request,
                canonicalReviewExists: context.canonicalReviewExists,
                currentUserId: currentUser.uid,
                userProfile,
                currentPathname: location.pathname,
                currentSearch: location.search,
                nowMs,
              })
          );
        })
        .sort((left, right) => {
          const leftCreatedAt = getReviewInvitationCreatedAtMs(left) ?? Number.MAX_SAFE_INTEGER;
          const rightCreatedAt = getReviewInvitationCreatedAtMs(right) ?? Number.MAX_SAFE_INTEGER;
          return leftCreatedAt - rightCreatedAt || left.id.localeCompare(right.id);
        })[0] || null
    );
  }, [
    contexts,
    currentUser?.uid,
    invitations,
    isActiveClient,
    location.pathname,
    location.search,
    nowMs,
    userProfile,
  ]);

  useEffect(() => {
    registerCandidate({
      id: 'review-invitation',
      priority: 100,
      wantsOpen: Boolean(selectedInvitation),
    });
  }, [registerCandidate, selectedInvitation]);

  useEffect(() => () => unregisterCandidate('review-invitation'), [unregisterCandidate]);

  useEffect(() => {
    setSnoozeError(null);
    setSnoozingRequestId(null);
  }, [selectedInvitation?.id]);

  if (!selectedInvitation || activePromptId !== 'review-invitation') {
    return null;
  }

  const requestId = selectedInvitation.id;
  const request = contexts[requestId]?.request;
  const serviceName = String(
    (request as (RepairRequest & { serviceName?: unknown }) | null)?.serviceName ||
      request?.serviceId ||
      request?.serviceProviderId ||
      'serwis'
  );

  const goToReview = () => {
    navigate(`/app/request/${requestId}?section=review`);
  };

  const snooze = async () => {
    if (snoozingRequestId) return;
    setSnoozeError(null);
    setSnoozingRequestId(requestId);
    try {
      await reviewInvitationService.snoozeReviewInvitation({ requestId });
    } catch (error) {
      console.error('[ReviewInvitationPrompt] Snooze failed.', error);
      setSnoozeError('Nie udało się odroczyć zaproszenia. Spróbuj ponownie.');
    } finally {
      setSnoozingRequestId(null);
    }
  };

  return (
    <Modal isOpen onClose={() => void snooze()} ariaLabelledBy={modalTitleId}>
      <div className="p-5 md:p-6">
        <h3 id={modalTitleId} className="text-lg md:text-xl font-semibold text-primary-light-100">
          Oceń zakończone zlecenie
        </h3>
        <p className="mt-2 text-sm text-primary-light-300">
          Zlecenie dla <span className="text-primary-light-100 font-medium">{serviceName}</span>{' '}
          jest zakończone. Dodaj krótką opinię, aby pomóc innym klientom.
        </p>
        {snoozeError && (
          <p className="mt-3 text-sm text-status-error-300" role="alert">
            {snoozeError}
          </p>
        )}
        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <Button
            variant="primary"
            onClick={goToReview}
            className="w-full sm:w-auto"
            disabled={Boolean(snoozingRequestId)}
          >
            Wystaw opinię
          </Button>
          <Button
            variant="neutral"
            onClick={() => void snooze()}
            className="w-full sm:w-auto"
            disabled={Boolean(snoozingRequestId)}
          >
            Później
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReviewInvitationPrompt;
